import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import { Card, Button } from "components/ui";
import {
  TicketIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { getQueriesByLeadId } from "services/queryService";
import { QueryCreationModal } from "components/query/QueryCreationModal";
import { hasPermission } from "utils/permissionsUtils";
import { formatDisplayDate } from "../../../utils/dateUtils";

const TicketsTab = ({ isMobile, leadId: propLeadId, isStudent = false }) => {
  const { id: routeLeadId } = useParams();
  const leadId = propLeadId || routeLeadId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightedQueryId = searchParams.get('queryId');
  const scrollRef = useRef(false);
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Helper function to convert text to proper case
  const toProperCase = (text) => {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  // Helper function to format status with proper case
  const formatStatus = (status) => {
    if (!status) return "Unknown";
    if (status.toLowerCase() === "in-progress") return "In Progress";
    return toProperCase(status);
  };

  // Helper function to format priority with proper case
  const formatPriority = (priority) => {
    if (!priority) return "Normal";
    return toProperCase(priority);
  };

  // Handle successful query creation
  const handleQueryCreated = () => {
    setShowCreateModal(false);
    // Refresh the queries list
    fetchQueries();
  };

  // Extract fetchQueries function for reuse
  const fetchQueries = async () => {
    if (!leadId) {
      setError("No lead ID provided");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await getQueriesByLeadId(leadId);

      if (result.success) {
        setQueries(result.data || []);
      } else {
        // Check if it's a "Queries not found" 404 error - treat as empty state, not error
        if (result.error &&
          (result.error.includes("Queries not found") ||
            result.error.includes("404") ||
            result.statusCode === 404)) {
          setQueries([]); // Set empty array instead of error
        } else {
          setError(result.error || "Failed to fetch queries");
        }
      }
    } catch (err) {
      console.error("Error fetching queries:", err);
      // Check if it's a 404 error in the caught exception
      if (err.response?.status === 404 ||
        err.message?.includes("Queries not found") ||
        err.message?.includes("404")) {
        setQueries([]); // Set empty array instead of error
      } else {
        setError("An unexpected error occurred while fetching queries");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueries();
  }, [leadId]);

  // Scroll logic for deep linking
  useEffect(() => {
    if (!loading && highlightedQueryId && queries.length > 0 && !scrollRef.current) {
      // Find exact case of query ID
      const targetQuery = queries.find(q => (q.QueryId || q.id)?.toLowerCase() === highlightedQueryId.toLowerCase());
      
      if (targetQuery) {
        const queryIdInDom = targetQuery.QueryId || targetQuery.id;
        const scrollAttempt = () => {
          const elementId = `query-${queryIdInDom}`;
          const element = document.getElementById(elementId);
          
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            scrollRef.current = true;
          } else {
            setTimeout(() => {
              const retryElement = document.getElementById(elementId);
              if (retryElement) {
                retryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                scrollRef.current = true;
              }
            }, 500);
          }
        };
        setTimeout(scrollAttempt, 500);
      }
    }
  }, [loading, queries, highlightedQueryId]);

  // Loading state removed - handled by global loader

  // Error state
  if (error) {
    return (
      <Card className={`${isMobile ? "p-4" : "p-8"} text-center`}>
        <div
          className={`${isMobile ? "h-12 w-12" : "h-16 w-16"} mx-auto mb-4 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20`}
        >
          <ExclamationTriangleIcon
            className={`${isMobile ? "h-6 w-6" : "h-8 w-8"} text-red-600 dark:text-red-400`}
          />
        </div>
        <h3
          className={`${isMobile ? "text-base" : "text-lg"} dark:text-dark-50 mb-2 font-semibold text-gray-900`}
        >
          Error Loading Queries
        </h3>
        <p
          className={`${isMobile ? "text-sm" : "text-base"} dark:text-dark-300 text-gray-500 ${isMobile ? "mb-4" : "mb-6"}`}
        >
          {error}
        </p>
        <Button
          variant="outlined"
          color="primary"
          className={isMobile ? "px-4 py-2 text-sm" : ""}
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Card>
    );
  }

  // No queries state
  if (queries.length === 0) {
    return (
      <>
        <Card className={`${isMobile ? "p-4" : "p-12"} text-center`}>
          <div
            className={`${isMobile ? "h-12 w-12" : "h-16 w-16"} dark:bg-dark-600 mx-auto mb-4 flex items-center justify-center rounded-full bg-gray-100`}
          >
            <TicketIcon
              className={`${isMobile ? "h-6 w-6" : "h-8 w-8"} dark:text-dark-400 text-gray-400`}
            />
          </div>
          <h3
            className={`${isMobile ? "text-base" : "text-lg"} dark:text-dark-50 mb-2 font-semibold text-gray-900`}
          >
            No Queries
          </h3>
          <p
            className={`${isMobile ? "text-sm" : "text-base"} dark:text-dark-300 text-gray-500 ${isMobile ? "mb-4" : "mb-6"}`}
          >
            No queries found for this lead. You can raise tickets or queries below.
          </p>
          {hasPermission("QMQC") ? <Button
            variant="outlined"
            color="primary"
            disabled={isStudent}
            className={`${isMobile ? "px-4 py-2 text-sm" : ""} ${isStudent ? "opacity-60 cursor-not-allowed" : ""}`}
            onClick={() => setShowCreateModal(true)}
          >
            Raise Ticket/Query
          </Button> : null}
        </Card>

        <QueryCreationModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleQueryCreated}
          preSelectedLeadId={leadId}
          restrictLeadSelection={true}
        />
      </>
    );
  }

  // Queries list
  return (
    <>
      <div className="space-y-4">
        <div className="mb-4 flex items-center justify-between">
          <h3
            className={`${isMobile ? "text-lg" : "text-xl"} font-semibold text-gray-900 dark:text-gray-100`}
          >
            Support Tickets ({queries.length})
          </h3>
          <Button
            variant="soft"
            color="primary"
            disabled={isStudent}
            className={`${isMobile ? "px-3 py-1 text-sm" : ""} ${isStudent ? "opacity-60 cursor-not-allowed" : ""}`}
            onClick={() => setShowCreateModal(true)}
          >
            Create New Ticket
          </Button>
        </div>

        {queries.map((query) => {
          const queryId = query.QueryId || query.id;
          const status = query.Status || query.status;
          const priority = query.Priority || query.priority;

          return (
            <Card
              key={queryId}
              id={`query-${queryId}`}
              className={`${isMobile ? "p-4" : "p-6"} transition-all duration-500`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="mb-2 flex items-center gap-3">
                    <h4
                      className={`${isMobile ? "text-base" : "text-lg"} font-semibold text-gray-900 dark:text-gray-100`}
                    >
                      {query.Subject || query.subject || "Untitled Query"}
                    </h4>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${status === "open"
                        ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                        : status === "in-progress"
                          ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400"
                          : "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400"
                        }`}
                    >
                      {formatStatus(status)}
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${priority === "high"
                        ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
                        : priority === "medium"
                          ? "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400"
                          : "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                        }`}
                    >
                      {formatPriority(priority)} Priority
                    </span>
                  </div>

                  <p
                    className={`${isMobile ? "text-sm" : "text-base"} mb-3 line-clamp-2 text-gray-600 dark:text-gray-400`}
                  >
                    {query.Description ||
                      query.description ||
                      "No description available"}
                  </p>

                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <span>QueryId: {query.DisplayQueryId || "N/A"}</span>
                    <span>
                      Category:{" "}
                      {query.CategoryName || query.categoryName || "Uncategorized"}
                    </span>
                    {(query.CreatedDate || query.createdDate) && (
                      <span>
                        Created:{" "}
                        {formatDisplayDate(query.CreatedDate || query.createdDate)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="ml-4">
                  <Button
                    variant="outlined"
                    color="primary"
                    className={isMobile ? "px-3 py-1 text-sm" : ""}
                    onClick={() => {
                      if (queryId) {
                        navigate(`/query/details/${queryId}`);
                      }
                    }}
                  >
                    View
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <QueryCreationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleQueryCreated}
        preSelectedLeadId={leadId}
        restrictLeadSelection={true}
      />
    </>
  );
};

export default TicketsTab;
